import {useCallback, useEffect, useState} from "react";
import {assignNullValueForEmptyValues, settingsDataFields} from "../Settings.model";
import {cancelSource, getCompanySettingsRequest, updateCompanySettingsRequest} from "../../../services/api";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useActions} from "../../../store/actions";
import {setTelegramTokenStatusAction} from "../../../store/actions/global";
import {settingsFormSchema} from "../../../components/ReactHookForm/schemes";
import {MESSAGES} from "../../../components/ReactHookForm/schemes/messages";
import {ERR_MESSAGES} from "../../../utils/constants";
import {getFormDataObj} from "../../../components/ReactHookForm/utils";

const useSettingsForm = () => {
  const setTelegramTokenStatus = useActions(setTelegramTokenStatusAction);
  const [settingsInfo, setSettingsInfo] = useState({companyName: null});

  const {handleSubmit, reset, setError, control, formState: {isDirty}} = useForm({
    mode: "onChange",
    resolver: yupResolver(settingsFormSchema),
    defaultValues: getFormDataObj({arrOfFields: settingsDataFields, serverData: {}})
  });

  const updateSettingsFormData = useCallback(async (data, stateOptions = {}) => {
    const formData = getFormDataObj({arrOfFields: settingsDataFields, serverData: data});

    await reset(formData, stateOptions);
    setSettingsInfo({companyName: formData.name});
    setTelegramTokenStatus(data.telegram_token_is_set);
  }, [setSettingsInfo, setTelegramTokenStatus, reset]);

  useEffect(() => {
    getCompanySettingsRequest()
      .then(({data}) => {
        updateSettingsFormData(data);
      });
    return () => {
      cancelSource.value?.cancel(ERR_MESSAGES.CANCELLED_REQUEST);
    };
  }, [reset, updateSettingsFormData]);

  const onFormSubmit = (formData) => {
    const newSettingsData = assignNullValueForEmptyValues(formData);
    return updateCompanySettingsRequest(newSettingsData)
      .then(({data}) => {
          updateSettingsFormData(data, {keepIsSubmitted: false});
        }
      )
      .catch((error) => {
        if (error.response.data.telegram_token) {
          setError("telegram_token",
            {type: "FieldError", message: MESSAGES.NO_TG_BOT},
            {shouldFocus: true}
          );
          window.scrollTo(0, 0);
          throw new Error();
        } else {
          throw new Error(Object.values(error.response.data).join(" \n"));
        }
      });
  };

  const resetTelegramToken = () => {
    return updateCompanySettingsRequest({
      telegram_token: null,
      is_telegram_auth_enabled: false
    })
      .then(({data}) => {
        updateSettingsFormData(data);
      });
  };

  return {
    onFormSubmit,
    settingsInfo,
    resetTelegramToken,
    form: {
      handleSubmit,
      control,
      isDirty
    }
  };
};

export default useSettingsForm;